import React, {useCallback, useEffect, useState} from "react";
import Map, {Source, Layer, MapRef, MapMouseEvent, Popup} from "react-map-gl";
import {useAtomValue} from "jotai";
import {FeatureCollection, MultiPolygon, Polygon, GeoJsonProperties, BBox} from "geojson";
import * as turf from "@turf/turf";
import {Box, Button, Stack, Typography} from "@mui/material";
import CenterLocationIcon from "@mui/icons-material/GpsNotFixed";
import {mapTokenLoadable} from "../../store/mapTokenStore";
import {MapSlider} from "./MapSlider";
import {MapDataType} from "../../types/mapDataType";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {FillLayerSpecification, SymbolLayerSpecification} from "mapbox-gl";
import {useMapWorldview} from "../../hooks/mapWorldviewHooks";

export interface Props {
    geojson: FeatureCollection<MultiPolygon | Polygon, GeoJsonProperties> | null;
    showRealign: boolean;
    showSlider: boolean;
    initView?: {latitude?: number; longitude?: number; zoom?: number};
    dataType?: MapDataType;
    enablePopup?: boolean;
}

interface HoverData {
    longitude: number;
    latitude: number;
    id: string;
    timezone?: number;
    label?: string;
}

export const IrritilesMap: React.FC<Props> = (props) => {
    const {t} = useTranslation();
    const {geojson, showRealign, showSlider, initView, dataType, enablePopup} = props;
    const [zoom, setZoom] = useState<number>(0);
    const mapToken = useAtomValue(mapTokenLoadable);
    const [mapRef, setMapRef] = useState<MapRef | null>(null);
    const [hoverInfo, setHoverInfo] = useState<HoverData | null>(null);
    const navigate = useNavigate();
    const {applyWorldviewFilters} = useMapWorldview();

    const handleMapRef = useCallback(
        (ref: MapRef) => {
            setMapRef(ref);
        },
        [setMapRef],
    );

    const onRealign = () => {
        if (mapRef && geojson) {
            if (!initView) {
                fitMapToBbox(mapRef, turf.bbox(geojson));
            }
        }
    };

    const onHover = useCallback(
        (event: MapMouseEvent) => {
            if (!enablePopup || !event.features?.[0]) {
                setHoverInfo(null);
                return;
            }
            const areas = event.features.map((feature) => turf.area(feature));
            const min = Math.min(...areas);
            const feature = event.features[areas.indexOf(min)];
            const props = feature?.properties ?? {};

            /* eslint-disable @typescript-eslint/no-unsafe-assignment */
            setHoverInfo({
                longitude: event.lngLat.lng,
                latitude: event.lngLat.lat,
                id: props.id,
                timezone: props.timezone,
                label: props.label,
            });
        },
        [enablePopup],
    );

    const onClick = useCallback(
        (event: MapMouseEvent) => {
            if (!(event.features?.[0] && dataType === MapDataType.IRRITILES)) {
                return;
            }
            const areas = event.features.map((feature) => turf.area(feature));
            const min = Math.min(...areas);
            const feature = event.features[areas.indexOf(min)];
            const props = feature?.properties ?? {};

            navigate({
                pathname: `/admin/irritiles/${props.id as string}`,
                search: location.search,
            });
        },
        [dataType, navigate],
    );

    useEffect(() => {
        if (mapRef && geojson && !initView) {
            fitMapToBbox(mapRef, turf.bbox(geojson));
        }
    }, [mapRef, geojson, initView]);

    useEffect(() => {
        if (mapRef) {
            applyWorldviewFilters(mapRef);
        }
    }, [mapRef, applyWorldviewFilters]);

    return (
        <Box sx={{height: "100%", width: "100%", position: "relative"}}>
            {mapToken.state === "hasData" && (
                <Map
                    initialViewState={{
                        latitude: initView?.latitude ?? 40,
                        longitude: initView?.longitude ?? 5,
                        zoom: initView?.zoom ?? 2,
                    }}
                    projection={{name: "globe"}}
                    reuseMaps
                    ref={handleMapRef}
                    mapStyle={getMapStyle()}
                    mapboxAccessToken={mapToken.data}
                    onZoom={(e) => setZoom(e.viewState.zoom)}
                    onMouseMove={onHover}
                    onClick={onClick}
                    interactiveLayerIds={["irritiles-layer", "fields-layer"]}
                >
                    {geojson !== null && dataType === MapDataType.IRRITILES && zoom > 7 && (
                        <>
                            <Source id="irritiles" type="geojson" data={geojson}>
                                <Layer {...IRRITILE_LAYER_STYLE} />
                                <Layer {...IRRITILE_SELECTED_LAYER_STYLE} filter={["in", "id", hoverInfo?.id ?? ""]} />
                            </Source>
                            {hoverInfo && (
                                <Popup
                                    longitude={hoverInfo.longitude}
                                    latitude={hoverInfo.latitude}
                                    closeButton={false}
                                    offset={10}
                                >
                                    <Stack direction="row">
                                        <Stack direction="column" sx={{mr: "0.75rem"}}>
                                            <Typography
                                                fontSize={12}
                                                fontWeight={500}
                                                color="#2B303F"
                                                textTransform="uppercase"
                                            >
                                                {t("map.irritiles_map.utm_zone")}:
                                            </Typography>
                                            <Typography
                                                fontSize={12}
                                                fontWeight={500}
                                                color="#2B303F"
                                                textTransform="uppercase"
                                            >
                                                {t("map.irritiles_map.id")}:
                                            </Typography>
                                        </Stack>
                                        <Stack direction="column">
                                            <Typography fontSize={12} fontWeight={300} color="#3A3F4F">
                                                {hoverInfo?.timezone ?? "n/c"}
                                            </Typography>
                                            <Typography fontSize={12} fontWeight={300} color="#3A3F4F">
                                                {hoverInfo?.id}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </Popup>
                            )}
                        </>
                    )}
                    {geojson !== null && dataType === MapDataType.IRRITILES && zoom <= 7 && (
                        <Source id="irritiles" type="geojson" data={geojson}>
                            <Layer {...IRRITILE_MARKER_LAYER_STYLE} />
                        </Source>
                    )}
                    {geojson !== null && dataType === MapDataType.IRRITILE && (
                        <>
                            <Source id="irritile" type="geojson" data={geojson}>
                                <Layer {...IRRITILE_SINGLE_LAYER_STYLE} filter={["in", "name", "irritile"]} />
                                <Layer {...FIELD_LAYER_STYLE} filter={["in", "name", "fields"]} />
                            </Source>
                            {hoverInfo && (
                                <Popup
                                    longitude={hoverInfo.longitude}
                                    latitude={hoverInfo.latitude}
                                    closeButton={false}
                                    offset={10}
                                >
                                    <Stack direction="row">
                                        <Stack direction="column" sx={{mr: "0.75rem"}}>
                                            <Typography
                                                fontSize={12}
                                                fontWeight={500}
                                                color="#2B303F"
                                                textTransform="uppercase"
                                            >
                                                {t("map.irritiles_map.name")}:
                                            </Typography>
                                            <Typography
                                                fontSize={12}
                                                fontWeight={500}
                                                color="#2B303F"
                                                textTransform="uppercase"
                                            >
                                                {t("map.irritiles_map.id")}:
                                            </Typography>
                                        </Stack>
                                        <Stack direction="column">
                                            <Typography fontSize={12} fontWeight={300} color="#3A3F4F">
                                                {hoverInfo?.label ?? "n/c"}
                                            </Typography>
                                            <Typography fontSize={12} fontWeight={300} color="#3A3F4F">
                                                {hoverInfo?.id}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </Popup>
                            )}
                        </>
                    )}
                </Map>
            )}
            <Box sx={{position: "absolute", top: 0, left: 0, marginTop: "1rem", marginLeft: "1rem"}}>
                <Stack direction="column" gap="1rem">
                    {showRealign && (
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={onRealign}
                            startIcon={<CenterLocationIcon />}
                            sx={{
                                "& .MuiButton-startIcon": {margin: 0},
                                minWidth: 0,
                                padding: "0.5rem",
                                width: "2rem",
                                height: "2rem",
                            }}
                        />
                    )}
                    {showSlider && <MapSlider zoom={zoom} onZoomChange={(value) => mapRef?.setZoom(value)} />}
                </Stack>
            </Box>
        </Box>
    );
};

const fitMapToBbox = (mapRef: MapRef, bbox: BBox, duration?: number) => {
    const [minLng, minLat, maxLng, maxLat] = bbox;
    mapRef.fitBounds(
        [
            [minLng, minLat],
            [maxLng, maxLat],
        ],
        {padding: 80, duration: duration ?? 1000},
    );
};

const getMapStyle = () => {
    return "mapbox://styles/mapbox/satellite-streets-v12";
};

const FIELD_LAYER_STYLE: FillLayerSpecification = {
    id: "fields-layer",
    type: "fill",
    source: "borders-source",
    paint: {
        "fill-color": "blue",
        "fill-opacity": 0.4,
    },
};

const IRRITILE_SINGLE_LAYER_STYLE: FillLayerSpecification = {
    id: "irritile-layer",
    source: "irritile-source",
    type: "fill",
    paint: {
        "fill-outline-color": "darkgreen",
        "fill-color": "#00CC00",
        "fill-opacity": 0.7,
    },
};

const IRRITILE_LAYER_STYLE: FillLayerSpecification = {
    id: "irritiles-layer",
    source: "irritiles-source",
    type: "fill",
    paint: {
        "fill-outline-color": "darkgreen",
        "fill-color": "#00CC00",
        "fill-opacity": 0.7,
    },
};

const IRRITILE_SELECTED_LAYER_STYLE: FillLayerSpecification = {
    id: "irritiles-layer-selected",
    source: "irritiles-layer-source",
    type: "fill",
    paint: {
        "fill-outline-color": "black",
        "fill-color": "#00CC00",
        "fill-opacity": 0.8,
    },
};

const IRRITILE_MARKER_LAYER_STYLE: SymbolLayerSpecification = {
    id: "irritiles-marker-layer",
    source: "irritiles-marker-source",
    type: "symbol",
    layout: {
        "icon-image": "rectangle-red-2",
    },
};
